/**
 * @file CatalogListTab.tsx
 * @description Вкладка «Список» панели «Каталог» (phase19.16 Р2а;
 * uiux-design-r2a §2): группы-карточки «Сетка · Шапка категории ·
 * Подкатегории · Фильтры · Сортировка» со сводкой у свёрнутой группы.
 * Содержимое групп — `CatalogListGroups`.
 *
 * SB122 (uiux-settings-groups §5, шаг 5; §8 — вид карточек): группы —
 * `SettingsGroup` в `SettingsGroups panel="catalog-list"`; на ПК все
 * открыты, сверху оглавление-пилюли, свёрнутые помнятся
 * (`commora.settings-groups.catalog-list`); до 768 — открыта одна;
 * `?group=<id>` открывает группу. Состав групп — реестром `GROUPS`.
 */

import { CATALOG_SORT_LABELS, type SiteCatalogOptions } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { FiltersGroup, GridGroup, SortGroup, SubcategoriesGroup, type CatalogGroupProps } from "./CatalogListGroups";
import { categoryHeaderSummary } from "./category-header-options";
import { CategoryHeaderGroup } from "./CategoryHeaderGroup";
import { gridHint } from "../../../lib/site-view/catalog-form";

type GroupKey = "grid" | "header" | "subcategories" | "filters" | "sort";

/** Сводки свёрнутых групп. */
function summaries(o: SiteCatalogOptions): Record<GroupKey, string> {
  const filters = [o.filters.categoryTree ? "категории" : null, o.filters.tags ? "метки" : null].filter(Boolean).join(", ");
  return {
    grid: `ПК ${o.grid.desktop} · планшет ${o.grid.tablet} · телефон ${o.grid.phone} · по ${o.perPage}`,
    header: categoryHeaderSummary(o),
    subcategories: o.subcategoryTiles.on ? "плитками" : "нет",
    filters: filters || "без фильтров",
    sort: CATALOG_SORT_LABELS[o.sorts.default].toLowerCase(),
  };
}

/** Группы вкладки: новая группа — запись реестра. */
const GROUPS: ReadonlyArray<{ key: GroupKey; title: string; tocLabel?: string; hint: string; Body: (props: CatalogGroupProps) => ReactNode }> = [
  { key: "grid", title: "Сетка", hint: "Сколько карточек в ряд и на странице.", Body: GridGroup },
  { key: "header", title: "Шапка категории", tocLabel: "Шапка", hint: "Верх страницы категории.", Body: CategoryHeaderGroup },
  { key: "subcategories", title: "Подкатегории", hint: "Плитки подкатегорий над товарами.", Body: SubcategoriesGroup },
  { key: "filters", title: "Фильтры", hint: "Что можно отфильтровать.", Body: FiltersGroup },
  { key: "sort", title: "Сортировка", hint: "Порядок товаров и выбор покупателя.", Body: SortGroup },
];

export function CatalogListTab({ options, onChange }: CatalogGroupProps) {
  const sum = summaries(options);
  // Узкие карточки при 4 в ряд — предупреждение в заголовке «Сетки».
  const gridWarning = gridHint(options) !== null;
  return (
    <SettingsGroups panel="catalog-list">
      {GROUPS.map(({ key, title, tocLabel, hint, Body }) => (
        <SettingsGroup
          key={key}
          id={key}
          title={title}
          tocLabel={tocLabel}
          hint={hint}
          summary={sum[key]}
          status={key === "grid" && gridWarning ? "warning" : undefined}
        >
          <Body options={options} onChange={onChange} />
        </SettingsGroup>
      ))}
    </SettingsGroups>
  );
}
